/**
 * Cursor Manager Utility
 * 
 * This utility helps manage map canvas cursor states to prevent
 * cursor conflicts and ensure proper cleanup when components unmount.
 */

class CursorManager {
  constructor() {
    this.activeCursorStates = new Map();
    this.defaultCursor = '';
  }

  /**
   * Set cursor for a specific component/context
   * @param {string} contextId - Unique identifier for the context (e.g., 'edit-fire', 'terrain-3d')
   * @param {string} cursor - Cursor style to set
   * @param {Object} map - Mapbox map instance
   */
  setCursor(contextId, cursor, map) {
    if (!map || !map.current) return;
    
    this.activeCursorStates.set(contextId, cursor);
    map.current.getCanvas().style.cursor = cursor;
  }

  /**
   * Reset cursor for a specific context
   * @param {string} contextId - Unique identifier for the context
   * @param {Object} map - Mapbox map instance
   */
  resetCursor(contextId, map) {
    if (!map || !map.current) return;
    
    this.activeCursorStates.delete(contextId);
    
    // If no other contexts are active, reset to default
    if (this.activeCursorStates.size === 0) {
      map.current.getCanvas().style.cursor = this.defaultCursor;
    }
  }

  /**
   * Force reset all cursor states
   * @param {Object} map - Mapbox map instance
   */
  forceResetAll(map) {
    if (!map || !map.current) return;
    
    this.activeCursorStates.clear();
    map.current.getCanvas().style.cursor = this.defaultCursor;
  }

  /**
   * Get current cursor state for debugging
   */
  getActiveStates() {
    return Array.from(this.activeCursorStates.entries());
  }
}

// Create a singleton instance
const cursorManager = new CursorManager();

export default cursorManager;
